import React, { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Loader2, Gift } from "lucide-react";
import SEO from "../components/SEO";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferralCode(ref.toUpperCase().trim());
  }, [searchParams]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await register(name, email, password, referralCode || null);
      if (referralCode) {
        toast.success("Account created — 20 bonus minutes added from referral!");
      } else {
        toast.success("Account created");
      }
      nav(u.role === "admin" ? "/app/admin" : "/app");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] grid-pattern px-6">
      <SEO title="Create Account" description="Create your free Project Wingman account and start practising AI-powered interviews today." path="/register" noIndex />
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 mb-10 justify-center">
          <img src="/wingman-logo.png" alt="Wingman.AI" className="h-8 w-auto" />
          <span className="font-display font-bold tracking-tight text-lg">WINGMAN<span className="text-[#D3FF24]">.</span>AI</span>
        </Link>
        <div className="border border-white/10 bg-[#0F0F0F] p-8">
          <h1 className="font-display text-3xl font-bold tracking-tight mb-1">Create account</h1>
          <p className="text-sm text-neutral-500 mb-8">Free forever, no credit card needed.</p>
          {referralCode && (
            <div className="flex items-center gap-3 border border-[#D3FF24]/30 bg-[#D3FF24]/5 px-4 py-3 mb-6 text-sm text-[#D3FF24]">
              <Gift className="w-4 h-4 shrink-0" />
              <span>Referral code <strong>{referralCode}</strong> applied — you'll get <strong>+20 bonus minutes</strong> on sign-up!</span>
            </div>
          )}
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Name</label>
              <input data-testid="register-name" required value={name} onChange={(e) => setName(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none" />
              <p className="mt-1.5 text-[10px] text-neutral-600">Max 25 words.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Email</label>
              <input data-testid="register-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Password</label>
              <input data-testid="register-password" type="password" required minLength={8} maxLength={128} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#0055FF] outline-none" />
              <p className="mt-1.5 text-[10px] text-neutral-600">Min. 8 characters with at least one letter and one number.</p>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">Referral Code (optional)</label>
              <input value={referralCode} onChange={(e) => setReferralCode(e.target.value.toUpperCase().trim())}
                placeholder="e.g. WMAB12CD"
                className="mt-2 w-full bg-black border border-white/10 px-3 py-2.5 text-sm focus:border-[#D3FF24] outline-none font-mono tracking-widest" />
              <p className="mt-1.5 text-[10px] text-neutral-600">Have a friend's code? Enter it to get +20 free bonus minutes.</p>
            </div>
            <button data-testid="register-submit" disabled={busy}
              className="w-full bg-[#D3FF24] text-black py-3 text-xs uppercase tracking-widest font-semibold hover:brightness-105 transition disabled:opacity-60 flex items-center justify-center gap-2">
              {busy && <Loader2 className="w-3.5 h-3.5 animate-spin" />} Create account
            </button>
          </form>
          <div className="mt-6 text-sm text-neutral-500 text-center">
            Already have an account? <Link to="/login" className="text-white hover:text-[#D3FF24]">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
